import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { PageCanvas } from './PageCanvas.jsx';
import { AnnotationLayer } from './AnnotationLayer.jsx';
import { TextLayer } from './TextLayer.jsx';
import { usePdf } from '../store/pdfStore.js';
import { useConfig } from '../store/configStore.js';
import { useT } from '../i18n/index.js';
import { Icon } from './Icon.jsx';

export function AllPagesView({ doc }) {
  const t = useT();
  const pages = usePdf((s) => s.pages);
  const renderScale = useConfig((s) => s.config.settings.renderScale);
  const editorDefaults = useConfig((s) => s.config.settings.editor);

  if (!pages.length) return <div className="empty">{t('editor.no_pages')}</div>;

  return (
    <div className="all-pages">
      {pages.map((p, i) => (
        <PageSlot
          key={p.id}
          doc={doc}
          page={p}
          index={i}
          total={pages.length}
          scale={renderScale}
          defaults={{
            font: editorDefaults.defaultFont,
            size: editorDefaults.defaultFontSize,
            color: editorDefaults.defaultColor,
            bold: editorDefaults.defaultBold,
            italic: editorDefaults.defaultItalic,
          }}
        />
      ))}
    </div>
  );
}

function rotatedDims(page, scale) {
  const rot = ((page.rotation % 360) + 360) % 360;
  const w = rot === 90 || rot === 270 ? page.height : page.width;
  const h = rot === 90 || rot === 270 ? page.width : page.height;
  return { w: w * scale, h: h * scale };
}

const PageSlot = memo(function PageSlot({ doc, page, index, total, scale, defaults }) {
  const t = useT();
  const rotatePage = usePdf((s) => s.rotatePage);
  const duplicatePage = usePdf((s) => s.duplicatePage);
  const deletePage = usePdf((s) => s.deletePage);
  const openPage = usePdf((s) => s.openPage);
  const setCurrentPageId = usePdf((s) => s.setCurrentPageId);

  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [size, setSize] = useState(rotatedDims(page, scale));

  useEffect(() => {
    setSize(rotatedDims(page, scale));
  }, [page.width, page.height, page.rotation, scale]);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin: '600px 0px' }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          setCurrentPageId(page.id);
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [page.id, setCurrentPageId]);

  const onCanvasSize = (s) => setSize(s);

  return (
    <div className="page-slot" ref={ref} data-page-id={page.id}>
      <div className="page-slot-head">
        <span className="chip">{t('editor.page_indicator', { i: index + 1, n: total })}</span>
        <span className="chip">{page.rotation}°</span>
        <span style={{ marginLeft: 'auto' }} />
        <button className="btn icon sm" onClick={() => openPage(page.id)} title={t('toolbar.open_editor')}>
          <Icon name="eye" size={14} />
        </button>
        <button className="btn icon sm" onClick={() => rotatePage(page.id, -90)} title={t('toolbar.rotate_left')}>
          <Icon name="rotateCCW" size={14} />
        </button>
        <button className="btn icon sm" onClick={() => rotatePage(page.id, 90)} title={t('toolbar.rotate_right')}>
          <Icon name="rotateCW" size={14} />
        </button>
        <button className="btn icon sm" onClick={() => duplicatePage(page.id)} title={t('toolbar.duplicate')}>
          <Icon name="copy" size={14} />
        </button>
        <button className="btn icon sm danger" onClick={() => deletePage(page.id)} title={t('toolbar.delete')}>
          <Icon name="trash" size={14} />
        </button>
      </div>
      <div
        className="page-wrap"
        style={{ width: size.w, height: size.h }}
      >
        {visible ? (
          <>
            <PageCanvas
              doc={doc}
              originalIndex={page.originalIndex}
              rotation={page.rotation}
              scale={scale}
              onSize={(s) => onCanvasSize({ w: s.width, h: s.height })}
            />
            {page.rotation === 0 && (
              <TextLayer doc={doc} page={page} scale={scale} />
            )}
            <AnnotationLayer
              page={page}
              size={{ width: size.w, height: size.h }}
              scale={scale}
              defaults={defaults}
            />
          </>
        ) : (
          <div className="page-placeholder" style={{ width: size.w, height: size.h }} />
        )}
      </div>
    </div>
  );
});
